import React, { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Menu, Transition } from '@headlessui/react';
import { BuildingOfficeIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { TenantService } from '../services/TenantService';

interface TenantSwitcherProps {
  /** Tenant currently in scope, or undefined when no tenant is drilled into. */
  currentTenantId?: string | undefined;
  /**
   * First path segment after /tenants/:id (e.g. 'expenses'), carried across the
   * switch so picking another tenant lands on the same section rather than
   * bouncing back to its dashboard. Deeper segments are dropped — an entity id
   * from one tenant is meaningless in another.
   */
  section?: string | undefined;
  /** Called after navigating, so the mobile drawer can close itself. */
  onNavigate?: (() => void) | undefined;
}

/**
 * Tenant picker for superadmins. Their own tenantId is the 'system' pseudo-tenant,
 * so the sidebar's tenant-scoped sections stay hidden until a real tenant is in
 * scope; without this they had to go via Tenants List every time.
 */
export const TenantSwitcher: React.FC<TenantSwitcherProps> = ({
  currentTenantId,
  section,
  onNavigate,
}) => {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['tenant-switcher'],
    queryFn: () => TenantService.getTenants({ limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  const tenants = data?.data?.data || [];
  const current = tenants.find(t => t.id === currentTenantId);

  const label = current?.name || (currentTenantId ? 'Unknown tenant' : 'Select a tenant');

  const go = (tenantId: string) => {
    navigate(section ? `/tenants/${tenantId}/${section}` : `/tenants/${tenantId}`);
    onNavigate?.();
  };

  return (
    <Menu as="div" className="relative px-2">
      <Menu.Button className="w-full flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500">
        <BuildingOfficeIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
        <span
          className={`flex-1 truncate ${current ? 'font-medium text-gray-900' : 'text-gray-500'}`}
        >
          {isLoading ? 'Loading tenants…' : label}
        </span>
        <ChevronUpDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute left-2 right-2 z-20 mt-1 max-h-72 overflow-y-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          {tenants.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">
              {isLoading ? 'Loading…' : 'No tenants found.'}
            </div>
          )}
          {tenants.map(tenant => (
            <Menu.Item key={tenant.id}>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => go(tenant.id)}
                  className={`${
                    active ? 'bg-gray-50' : ''
                  } flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700`}
                >
                  <span className="w-10 flex-shrink-0 rounded bg-indigo-50 px-1 py-0.5 text-center text-xs font-semibold text-indigo-600">
                    {tenant.code}
                  </span>
                  <span className="flex-1 truncate">{tenant.name}</span>
                  {tenant.id === currentTenantId && (
                    <CheckIcon className="h-4 w-4 flex-shrink-0 text-indigo-600" />
                  )}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default TenantSwitcher;
